import {Box, Button, Flex, Grid, Text} from "@chakra-ui/react";
import {AppContext, AppContextI} from "../../context/ContextWrapper";
import {useContext, useEffect, useRef, useState} from "react";
import {
    BeaconTrigger,
    GpsShapeType,
    GpsTrigger,
    LatLong,
    MarkerSpot,
    PolygonSpot,
    Spot,
    SpotType,
    Trigger,
    WiFiApTrigger
} from "../../types";
import BeaconTriggerDetails from "./BeaconTriggerDetails";
import {v4 as uuidv4} from 'uuid';
import WiFiApTriggerDetails from "./WiFiApTriggerDetails";
import GpsTriggerDetails from "./GpsTriggerDetails";
import {MapContext, MapContextI} from "../../context/MapContextWrapper";
import ManagedCheckBox from "../../form/ManagedCheckBox";
import HelpTooltip from "../../help/HelpTooltip";
import DraftBeaconTriggerDetails from "./DraftBeaconTriggerDetails";


type Props = {
    spot: Spot,
}

const SpotListTriggerList = ({spot}: Props) => {
    const appContext: AppContextI = useContext<AppContextI>(AppContext);
    const mapContext: MapContextI = useContext<MapContextI>(MapContext);
    const isOnDemandMode = appContext.isOnDemand();
    const bleEnabled = appContext.isBleEnabled();
    const [synchronizeEntry, setSynchronizeEntry] = useState<boolean>(spot.synchronizeEntry ?? false);
    const [pendingBeaconIds, setPendingBeaconIds] = useState<string[]>([]);

    const spotRef = useRef(spot);
    const updateSpotRef = useRef(appContext.updateSpot);
    spotRef.current = spot;
    updateSpotRef.current = appContext.updateSpot;

    const markerSpot: MarkerSpot = spot as MarkerSpot;
    const getLatestMarkerSpot = () => (appContext.getSpot(spot.id) as MarkerSpot) || markerSpot;

    function onGpsTriggerDelete(object: Spot, trigger: GpsTrigger) {
        const gpsTriggers = (object.gpsTriggers || []).filter((t: GpsTrigger) => t.id !== trigger.id);

        if (trigger.shapeType === GpsShapeType.Circle) {
            // Circle trigger lives on a Marker spot — just remove the trigger
            appContext.updateSpot(object, {
                ...object,
                gpsTriggers
            } as MarkerSpot);
            return;
        }

        const coords = trigger.polygonCoords || (object as PolygonSpot).coords || [];
        const centroid: LatLong = coords.length > 0
            ? {
                lat: coords.reduce((sum, c) => sum + c.lat, 0) / coords.length,
                lng: coords.reduce((sum, c) => sum + c.lng, 0) / coords.length
            }
            : {lat: 0, lng: 0};

        const {coords: _coords, ...rest} = object as PolygonSpot;
        appContext.updateSpot(object, {
            ...rest,
            type: SpotType.Marker,
            position: centroid,
            gpsTriggers
        } as MarkerSpot);
    }

    useEffect(() => {
        setSynchronizeEntry(spot.synchronizeEntry ?? false);
    }, [spot.id, spot.synchronizeEntry]);

    useEffect(() => {
        const currentSpot = spotRef.current;
        if (synchronizeEntry !== (currentSpot.synchronizeEntry ?? false)) {
            updateSpotRef.current(currentSpot, {
                ...currentSpot,
                synchronizeEntry: synchronizeEntry
            } as Spot);
        }
    }, [synchronizeEntry]);

    useEffect(() => {
        setPendingBeaconIds([]);
    }, [spot.id]);

    return <>
        {!isOnDemandMode ? (
            <Box marginBottom={"12px"}>
                <ManagedCheckBox name={"synchronizeEntry"} defaultValue={synchronizeEntry} onChange={setSynchronizeEntry}
                                 disabled={!appContext.isDraft()}>
                    <Flex alignItems={"center"} columnGap={"6px"} rowGap={"4px"} flexWrap={"wrap"}>
                        <HelpTooltip content={"Start entry playback in sync with other spots that share this group. Only spots triggered within a tight window will align to the same start point."}>
                            <Text>Trigger by group synchronization</Text>
                        </HelpTooltip>
                        <HelpTooltip content={"Group synchronization requires the USB sync dongle to be attached to the device running the app. Without it, sounds play independently for each in the group."}
                                     placement={"right"}
                                     icon
                                     iconAriaLabel={"Synchronization requirements"}/>
                    </Flex>
                </ManagedCheckBox>
            </Box>
        ) : null}
        <Grid templateColumns={"min-content 1fr min-content"} marginTop={"8px"}>
            {
                appContext.isSelected(spot) ?
                    <>
                        <Box/>
                        <Flex columnGap={"4px"}>
                            {bleEnabled ? (
                                <Button size={"sm"}
                                        data-testid={"add-beacon-button"}
                                        onClick={() => {
                                            const pendingId = uuidv4();
                                            setPendingBeaconIds((prev) => [...prev, pendingId]);
                                        }
                                        }
                                        disabled={!appContext.isDraft()}>
                                    Add Beacon
                                </Button>
                            ) : null}

                            <Button size={"sm"}
                                    onClick={() => {
                                        const latestMarkerSpot = getLatestMarkerSpot();
                                        const newTriggers: Trigger[] = [...latestMarkerSpot.wiFiTriggers ? latestMarkerSpot.wiFiTriggers : [],
                                            {
                                                type: "WiFiAP",
                                                id: uuidv4(),
                                                name: ""
                                            } as WiFiApTrigger
                                        ];
                                        appContext.updateSpot(latestMarkerSpot, {
                                            ...latestMarkerSpot,
                                            wiFiTriggers: newTriggers
                                        } as MarkerSpot)
                                    }
                                    }
                                    disabled={!appContext.isDraft()}>
                                Add WiFi AP
                            </Button>

                            <Button size={"sm"}
                                    disabled={!appContext.isDraft() || (spot.gpsTriggers != null && spot.gpsTriggers.length > 0)}
                                    onClick={() => {
                                        const center = markerSpot.position || mapContext.getMapCenter() || {lat: 0, lng: 0};
                                        const latSpan = mapContext.getMapLatSpan() || 0.001;
                                        // For this triangle template, vertical height is 1.5 * offset.
                                        // Target initial polygon height at about 20% of visible map height.
                                        const offset = latSpan * (0.2 / 1.5);
                                        const defaultCoords: LatLong[] = [
                                            {lat: center.lat + offset, lng: center.lng},
                                            {lat: center.lat - offset / 2, lng: center.lng - offset},
                                            {lat: center.lat - offset / 2, lng: center.lng + offset}
                                        ];
                                        const newTrigger: GpsTrigger = {
                                            id: uuidv4(),
                                            type: "GPS",
                                            shapeType: GpsShapeType.Polygon,
                                            polygonCoords: defaultCoords
                                        };
                                        appContext.updateSpot(spot, {
                                            ...spot,
                                            type: SpotType.Polygon,
                                            coords: defaultCoords,
                                            gpsTriggers: [newTrigger]
                                        } as unknown as PolygonSpot)
                                    }
                                    }
                            >
                                Add GPS area
                            </Button>

                            <Button size={"sm"}
                                    disabled={!appContext.isDraft() || (spot.gpsTriggers != null && spot.gpsTriggers.length > 0)}
                                    onClick={() => {
                                        const latSpan = mapContext.getMapLatSpan() || 0.001;
                                        // Default radius: ~10% of visible map height in meters
                                        const defaultRadius = Math.max(10, latSpan * 111_320 * 0.1);
                                        const newTrigger: GpsTrigger = {
                                            id: uuidv4(),
                                            type: "GPS",
                                            shapeType: GpsShapeType.Circle,
                                            circleRadius: Math.round(defaultRadius)
                                        };
                                        appContext.updateSpot(spot, {
                                            ...spot,
                                            gpsTriggers: [newTrigger]
                                        } as MarkerSpot)
                                    }
                                    }
                            >
                                Add GPS circle
                            </Button>
                        </Flex>
                    </>
                    : null
            }
        </Grid>
        <Grid gridTemplateColumns={"max-content 1fr 24px max-content"} marginTop={"8px"} columnGap={2}>
            {bleEnabled ? (
                <>
                    {
                        pendingBeaconIds.map((pendingId) => (
                            <DraftBeaconTriggerDetails
                                key={`beacon-draft-${pendingId}`}
                                onCreate={(payload) => {
                                    const latestMarkerSpot = getLatestMarkerSpot();
                                    const draftTrigger: BeaconTrigger = payload.type === "Beacon"
                                        ? {
                                            id: pendingId,
                                            type: "Beacon",
                                            macAddr: payload.macAddr,
                                            majorId: "",
                                            minorId: ""
                                        } as BeaconTrigger
                                        : {
                                            id: pendingId,
                                            type: "iBeacon",
                                            macAddr: "",
                                            uuid: payload.uuid,
                                            majorId: payload.majorId,
                                            minorId: payload.minorId
                                        } as BeaconTrigger;
                                    const newTriggers: Trigger[] = [draftTrigger, ...(latestMarkerSpot.beaconTriggers || [])];
                                    appContext.updateSpot(latestMarkerSpot, {
                                        ...latestMarkerSpot,
                                        beaconTriggers: newTriggers
                                    } as MarkerSpot);
                                    setPendingBeaconIds((prev) => prev.filter((id) => id !== pendingId));
                                }}
                                onCancel={() => {
                                    setPendingBeaconIds((prev) => prev.filter((id) => id !== pendingId));
                                }}
                                disabled={!appContext.isDraft()}
                            />
                        ))
                    }
                    {
                        markerSpot.beaconTriggers?.map((beaconTrigger: BeaconTrigger, index: number) =>
                            <BeaconTriggerDetails updateCall={appContext.updateSpot}
                                                  key={"beacon_" + beaconTrigger.id}
                                                  trigger={beaconTrigger}
                                                  object={markerSpot} index={index}/>
                        )
                    }
                </>
            ) : null}
            {
                markerSpot.wiFiTriggers?.map((wiFiApTrigger: WiFiApTrigger, index: number) =>
                    <WiFiApTriggerDetails updateCall={appContext.updateSpot}
                                          key={"wifiAp_" + wiFiApTrigger.id}
                                          trigger={wiFiApTrigger}
                                          object={markerSpot} index={index}/>
                )
            }
            {
                spot.gpsTriggers?.map((gpsTrigger: GpsTrigger, index: number) =>
                    <GpsTriggerDetails updateCall={appContext.updateSpot}
                                       key={"gps_" + gpsTrigger.id}
                                       trigger={gpsTrigger}
                                       object={spot} index={index}
                                       onDelete={(obj, trig) => onGpsTriggerDelete(obj as Spot, trig)}
                    />
                )
            }
        </Grid>
    </>
}

export default SpotListTriggerList;
